/* eslint-disable no-param-reassign */
import { ApolloError } from '@apollo/client';
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Skeleton,
  CloseButton,
  Checkbox,
  Button,
  Link,
  useToast,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  VisibilityState,
  Row,
} from '@tanstack/react-table';
import React, {
  useCallback,
  useEffect,
  useState,
  useReducer,
  useMemo,
  memo,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { Util, DataLake } from '@finley-technologies/finley-types';

import {
  AthenaTableGQLType,
  useGetLatestHistoricalReceivableResponseTapeValuesLazyQueryGQLType,
  useGetLatestHistoricalReceivableResponseTapeValuesCountLazyQueryGQLType,
  ReceivableResponseTapeAthenaPaginatedResponseGQLType,
  AssignmentConfirmationJobGQLType,
  ReceivablePaginationMetadataGQLType,
  useInitiateReceivableReassignmentFromAthenaQueryMutation,
  GetAthenaSchemaForOrganizationQueryGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { ActionsDrawer } from './actionsDrawer/ActionsDrawer';
import { BulkActionsButton } from './BulkActionsButton';
import { ColumnSelector } from './ColumnSelector';
import { FilterDrawer } from './FilterDrawer';
import { ReceivablesTablePaginationControl } from './Pagination';
import { QueryFiltersPanel } from './QueryFilterPills';
import {
  DateRange,
  generateInitialDateRange,
  ReceivablesTableColumnDef,
  useTransformAthenaColumnsToColumnConfig,
  initializeReceivableResponseTapeQueryFilterLocalStorage,
  deactivateAllReceivableResponseTapeQueryFiltersInLocalStorage,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { formatAthenaSqlString } from 'packages/common-frontend/components/QueryBuilder';
import {
  frozenHeaderCellStyling,
  frozenHeaderRowStyling,
  frozenRowCellStyling,
  frozenTableStyling,
} from 'packages/common-frontend/styles';
import { usePollRepurchaseAndTransferJobs } from 'packages/loanProduct/hooks/usePollRepurchaseAndTransferJobs';
import { useGetAthenaSchemaForOrganizationQuery } from 'packages/organization/requests/organization.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import {
  errorToastId,
  errorToastConfig,
} from '~/packages/common-frontend/constants';

const parseReceivablesData = (
  data: Util.Maybe<ReceivableResponseTapeAthenaPaginatedResponseGQLType>,
) => {
  if (!data)
    return {
      receivables: [],
      paginationMetadata: {
        queryPageNumber: 1,
        hasNextPage: false,
        queryPageBoundaryReceivableIds: [],
      },
    };

  const { receivables: receivablesData, paginationMetadata } = data;

  const receivables = JSON.parse(receivablesData) ?? [];
  return { receivables, paginationMetadata };
};

const getInitialColumnVisibility = (
  columns: ReceivablesTableColumnDef[],
): VisibilityState => {
  const output: VisibilityState = {};
  columns.forEach((columnDef) => {
    output[columnDef.meta.key] = columnDef.meta.initiallyVisible;
  });
  return output;
};

type ConditionalWrapperProps = {
  condition: boolean;
  wrapper: (children: ReactNode) => React.JSX.Element;
  children: ReactNode;
};

const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: ConditionalWrapperProps) => {
  return condition ? wrapper(children) : <>{children}</>;
};

// eslint-disable-next-line react/display-name
const MemoizedCells = memo(
  // passing columnVisibility because we want to rerender the cells when the column visibility changes
  // eslint-disable-next-line react/no-unused-prop-types
  ({ row }: { row: Row<any>; columnVisibility: Record<string, boolean> }) => {
    return (
      <>
        {row
          .getVisibleCells()
          .filter((cell) => cell.column.id !== 'externalreceivableid')
          .map((cell) => {
            return (
              <Td
                key={`${cell.id}+${row.original.externalreceivableid}`}
                textAlign={cell.column.columnDef.meta?.style?.textAlign}
                padding={
                  cell.column.columnDef.meta?.style?.padding || '8px 16px'
                }
                height="56px"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Td>
            );
          })}
      </>
    );
  },
);

// eslint-disable-next-line react/display-name
const MemoizedRow = memo(
  ({
    row,
    isChecked,
    dispatchRowSelection,
    allQueryRowsSelected,
    showCheckbox,
    checkboxDisabled,
    columnVisibility,
  }: {
    row: Row<any>;
    isChecked: boolean;
    dispatchRowSelection: (value: any) => void;
    allQueryRowsSelected: boolean;
    showCheckbox: boolean;
    checkboxDisabled: boolean;
    columnVisibility: Record<string, boolean>;
  }) => {
    return (
      <Tr key={row.original.externalreceivableid}>
        {row
          .getVisibleCells()
          .filter((cell) => cell.column.id === 'externalreceivableid')
          .map((cell) => {
            return (
              <Td
                key={`${cell.id}+${row.original.externalreceivableid}`}
                textAlign={cell.column.columnDef.meta?.style?.textAlign}
                padding={
                  cell.column.columnDef.meta?.style?.padding || '8px 16px'
                }
                height="56px"
                style={frozenRowCellStyling(true)}
              >
                <ConditionalWrapper
                  condition={showCheckbox}
                  wrapper={(children) => (
                    <Checkbox
                      isDisabled={checkboxDisabled}
                      isChecked={isChecked}
                      onChange={() =>
                        dispatchRowSelection({
                          type: 'TOGGLE_ROW',
                          rowId: row.original.finleyreceivableid,
                          allQueryRowsSelected,
                        })
                      }
                      spacing="2rem"
                    >
                      {children}
                    </Checkbox>
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </ConditionalWrapper>
              </Td>
            );
          })}
        <MemoizedCells row={row} columnVisibility={columnVisibility} />
      </Tr>
    );
  },
);

type ReceivablesTableBodyProps = {
  organizationId: string;
  creditFacilityId: string;
  columns: ReceivablesTableColumnDef[];
  receivables: any[];
  paginationMetadata: ReceivablePaginationMetadataGQLType;
  reportingDatesRange: DateRange;
  setDateRangeFilter: React.Dispatch<React.SetStateAction<DateRange>>;
  refetchReceivables: ({
    queryPageNumber,
    queryPageBoundaryReceivableIds,
  }: {
    queryPageNumber: number;
    queryPageBoundaryReceivableIds: Util.Maybe<string>[];
  }) => void;
  receivablesError?: ApolloError;
  isLoading: boolean;
  receivablesTableSchema: AthenaTableGQLType;
  isLenderPage: boolean;
  setInitiatedJobInfo: React.Dispatch<
    React.SetStateAction<{
      id: string;
      jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
      receivableCount: number | null;
    } | null>
  >;
  jobInProgress: boolean;
  queryReceivablesCount: number | null;
  initiateReassignmentConfirmationJob: ({
    rowSelectionData,
    areAllQueryRowsSelected,
    lpCfReassignmentInfo,
    jobType,
  }: {
    rowSelectionData: any;
    areAllQueryRowsSelected: boolean;
    lpCfReassignmentInfo: {
      loanProductId: string;
      creditFacilityId: string | null;
    }[];
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
  }) => void;
  loadingInitialReceivables: boolean;
  loanProductId: string;
  refetchActiveQueryFilters: () => void;
};
export const ReceivablesTableBody = ({
  organizationId,
  creditFacilityId,
  loanProductId,
  receivables,
  columns,
  paginationMetadata,
  reportingDatesRange,
  setDateRangeFilter,
  refetchReceivables,
  receivablesError,
  isLoading,
  receivablesTableSchema,
  isLenderPage,
  setInitiatedJobInfo,
  jobInProgress,
  queryReceivablesCount,
  initiateReassignmentConfirmationJob,
  loadingInitialReceivables,
  refetchActiveQueryFilters,
}: ReceivablesTableBodyProps) => {
  const [allQueryRowsSelected, setAllQueryRowsSelected] = useState<boolean>(
    false,
  );
  const [queryRowsSelectedCount, setQueryRowsSelectedCount] = useState<number>(
    0,
  );

  const rowSelectionReducer = (state: any, action: any) => {
    switch (action.type) {
      case 'TOGGLE_ROW': {
        const newState = { ...state };
        let rowSelectedDelta = 0;
        if (action.allQueryRowsSelected && state[action.rowId] === undefined) {
          newState[action.rowId] = false;
          rowSelectedDelta = -1;
        } else if (
          action.allQueryRowsSelected &&
          state[action.rowId] === false
        ) {
          delete newState[action.rowId];
          rowSelectedDelta = 1;
        } else if (!action.allQueryRowsSelected && state[action.rowId]) {
          delete newState[action.rowId];
          rowSelectedDelta = -1;
        } else if (!action.allQueryRowsSelected && !state[action.rowId]) {
          newState[action.rowId] = true;
          rowSelectedDelta = 1;
        }
        setQueryRowsSelectedCount((oldCount) => oldCount + rowSelectedDelta);
        return newState;
      }
      case 'TOGGLE_PAGE_ALL': {
        let selectedCountChange = 0;
        const result = action.allRowIds.reduce(
          (acc: { [id: string]: boolean }, rowId: string) => {
            const initState = acc[rowId];

            // select all = true & page all = false
            if (action.allQueryRowsSelected && !action.isAllPageRowsSelected) {
              if (initState !== false) {
                selectedCountChange -= 1;
              }
              acc[rowId] = false;
            } else if (
              // select all = true & page all = true
              action.allQueryRowsSelected &&
              action.isAllPageRowsSelected
            ) {
              if (initState === false) {
                selectedCountChange += 1;
              }
              delete acc[rowId];
            } else if (
              // select all = false & page all = false
              !action.allQueryRowsSelected &&
              !action.isAllPageRowsSelected
            ) {
              if (initState === true) {
                selectedCountChange -= 1;
              }
              delete acc[rowId];
            } else if (
              // select all = false & page all = true
              !action.allQueryRowsSelected &&
              action.isAllPageRowsSelected
            ) {
              if (initState !== true) {
                selectedCountChange += 1;
              }
              acc[rowId] = true;
            }
            return acc;
          },
          { ...state },
        );
        setQueryRowsSelectedCount((oldCount) => oldCount + selectedCountChange);
        return result;
      }
      case 'SELECT_ALL':
        setQueryRowsSelectedCount(action.queryReceivablesCount);
        setAllQueryRowsSelected(true);
        return {};
      case 'CLEAR_ALL':
        setQueryRowsSelectedCount(0);
        setAllQueryRowsSelected(false);
        return {};

      default:
        return state;
    }
  };

  const [rowSelection, dispatchRowSelection] = useReducer(
    rowSelectionReducer,
    {},
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(
    columns.reduce((acc, columnDef) => {
      if (columnDef.id) {
        acc[columnDef.id] = columnDef.meta?.initialVisibility;
      }
      return acc;
    }, {} as Record<string, boolean>),
  );

  const table = useReactTable({
    data: receivables,
    columns,
    state: {
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
      columnVisibility: getInitialColumnVisibility(columns),
    },
    autoResetPageIndex: false,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.finleyreceivableid,
    onRowSelectionChange: dispatchRowSelection,
  });

  useEffect(() => {
    if (loadingInitialReceivables) {
      dispatchRowSelection({
        type: 'CLEAR_ALL',
      });
      table.setPageIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInitialReceivables]);

  const pageIndex = table.getState().pagination.pageIndex;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedRows = useMemo(() => table.getRowModel().rows, [
    table,
    receivables,
    pageIndex,
  ]);

  const allRowIds = memoizedRows.map((row) => row.id);
  const areAllPageRowsSelected =
    table.getIsAllPageRowsSelected() ||
    (allQueryRowsSelected &&
      allRowIds.every((rowId) => rowSelection[rowId] !== false));
  const areSomePageRowsSelected =
    !areAllPageRowsSelected &&
    (table.getIsSomePageRowsSelected() ||
      (allQueryRowsSelected &&
        allRowIds.some((rowId) => rowSelection[rowId] !== false)));

  return (
    <Box>
      <Flex
        justifyContent={!isLenderPage ? 'space-between' : 'flex-end'}
        mt="32px"
      >
        {!isLenderPage && (
          <Flex gap="24px">
            <Button
              variant="link"
              colorScheme="blue"
              cursor="pointer"
              onClick={() => {
                dispatchRowSelection({
                  type: 'CLEAR_ALL',
                });
              }}
              isDisabled={isLoading || jobInProgress}
            >
              Clear All
            </Button>
            <Button
              variant="link"
              colorScheme="blue"
              cursor="pointer"
              onClick={() => {
                if (queryReceivablesCount !== null) {
                  dispatchRowSelection({
                    type: 'SELECT_ALL',
                    queryReceivablesCount,
                  });
                }
              }}
              isDisabled={
                queryReceivablesCount === null || isLoading || jobInProgress
              }
            >
              Select All
            </Button>
            <BulkActionsButton
              queryRowsSelectedCount={queryRowsSelectedCount}
              jobInProgress={jobInProgress}
              confirmAction={(
                lpCfReassignmentInfo: {
                  loanProductId: string;
                  creditFacilityId: string | null;
                }[],
                jobType: Omit<
                  AssignmentConfirmationJobGQLType['jobType'],
                  'assignment'
                >,
              ) => {
                initiateReassignmentConfirmationJob({
                  rowSelectionData: rowSelection,
                  areAllQueryRowsSelected: allQueryRowsSelected,
                  lpCfReassignmentInfo,
                  jobType,
                });
                dispatchRowSelection({
                  type: 'CLEAR_ALL',
                });
              }}
              creditFacilityId={creditFacilityId}
              loanProductId={loanProductId}
            />
          </Flex>
        )}
        <Flex justifyContent="flex-end" alignItems="center" gap="1.5rem">
          {!isLenderPage && (
            <ActionsDrawer
              creditFacilityId={creditFacilityId}
              setInitiatedJobInfo={setInitiatedJobInfo}
              jobInProgress={jobInProgress}
            />
          )}
          <ColumnSelector tableInstance={table} />
          <FilterDrawer
            organizationId={organizationId}
            creditFacilityId={creditFacilityId}
            columnDefs={columns}
            initialDateRangeFilter={reportingDatesRange}
            onSetDateRange={setDateRangeFilter}
            receivablesTableSchema={receivablesTableSchema}
            refetchActiveQueryFilters={refetchActiveQueryFilters}
          />
        </Flex>
      </Flex>

      <QueryFiltersPanel
        organizationId={organizationId}
        refetchReceivables={() => {
          refetchReceivables({
            queryPageBoundaryReceivableIds: [],
            queryPageNumber: 1,
          });
        }}
      />

      <TableContainer
        mt="32px"
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
      >
        <Table style={frozenTableStyling}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {headerGroup.headers
                  .filter((header) => header.column.getIsVisible())
                  .map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      textTransform="capitalize"
                      padding={
                        header.column.columnDef.meta?.style?.padding ||
                        '8px 16px'
                      }
                      fontSize="16px"
                      fontWeight="medium"
                      whiteSpace="nowrap"
                      color={colors.black[95]}
                      borderBottom="2px solid"
                      height="64px"
                      style={frozenHeaderCellStyling(
                        header.id === 'externalreceivableid',
                      )}
                    >
                      <ConditionalWrapper
                        condition={
                          header.id === 'externalreceivableid' && !isLenderPage
                        }
                        wrapper={(children) => (
                          <Checkbox
                            spacing="2rem"
                            isDisabled={isLoading || jobInProgress}
                            isChecked={areAllPageRowsSelected}
                            isIndeterminate={areSomePageRowsSelected}
                            onChange={() =>
                              dispatchRowSelection({
                                type: 'TOGGLE_PAGE_ALL',
                                isAllPageRowsSelected: !areAllPageRowsSelected,
                                allRowIds,
                                allQueryRowsSelected,
                              })
                            }
                          >
                            {children}
                          </Checkbox>
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </ConditionalWrapper>
                    </Th>
                  ))}
              </Tr>
            ))}
          </Thead>
          {(isLoading || receivablesError) && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {columns.map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                      style={{
                        ...frozenRowCellStyling(
                          column.id === 'externalreceivableid',
                        ),
                        ...{
                          minWidth: [
                            'externalreceivableid',
                            'finleyreceivableid',
                            'loanproductid',
                          ].includes(column.meta.key.toLowerCase())
                            ? '350px'
                            : 'auto',
                        },
                      }}
                    >
                      <Skeleton h="100%" />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isLoading && !receivablesError && (
            <Tbody>
              {memoizedRows.map((row) => {
                return (
                  <MemoizedRow
                    key={row.original.finleyreceivableid}
                    row={row}
                    isChecked={
                      row.getIsSelected() ||
                      (allQueryRowsSelected && rowSelection[row.id] !== false)
                    }
                    dispatchRowSelection={dispatchRowSelection}
                    allQueryRowsSelected={allQueryRowsSelected}
                    showCheckbox={!isLenderPage}
                    checkboxDisabled={jobInProgress}
                    columnVisibility={columnVisibility}
                  />
                );
              })}
            </Tbody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && receivables.length === 0 && (
        <Alert mt="32px">
          <AlertIcon />
          <AlertDescription>
            No receivables match the selected filters.
          </AlertDescription>
        </Alert>
      )}
      <ReceivablesTablePaginationControl
        table={table}
        paginationMetadata={paginationMetadata}
        refetchReceivables={refetchReceivables}
      />
    </Box>
  );
};

type ReceivablesTableProps = {
  isLenderPage?: boolean;
  organizationId: string;
  creditFacilityId: string;
  loanProductId: string;
  implementationId: string;
  setReceivablesCount: React.Dispatch<React.SetStateAction<number | null>>;
  tableSchema?: GetAthenaSchemaForOrganizationQueryGQLType['athenaTables'][number];
};
const ReceivablesTableBase = ({
  isLenderPage = true,
  organizationId,
  creditFacilityId,
  loanProductId,
  implementationId,
  setReceivablesCount,
  tableSchema,
  activeQueryFilterStrings,
  refetchActiveQueryFilters,
}: ReceivablesTableProps & {
  activeQueryFilterStrings: string[];
  refetchActiveQueryFilters: () => void;
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [reportingDatesRange, setDateRangeFilter] = useState<DateRange>(
    generateInitialDateRange(),
  );

  // true when we are doing a brand new fetch of receivables, not fetching a new page or refetching a previous one
  const [
    loadingInitialReceivables,
    setLoadingInitialReceivables,
  ] = useState<boolean>(false);

  const [
    getReceivablesData,
    {
      data: receivablesData,
      error: receivablesError,
      loading: areReceivablesLoading,
    },
  ] = useGetLatestHistoricalReceivableResponseTapeValuesLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setLoadingInitialReceivables(false);
    },
    onError: () => {
      setLoadingInitialReceivables(false);
    },
  });

  const [
    getReceivablesCountData,

    {
      data: receivablesCountData,
      loading: isReceivablesCountLoading,
      error: receivablesCountError,
    },
  ] = useGetLatestHistoricalReceivableResponseTapeValuesCountLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setReceivablesCount(
        data.latestHistoricalReceivableResponseTapeValuesCount,
      );
    },
  });

  const { columns } = useTransformAthenaColumnsToColumnConfig({
    isLenderPage,
    columnProperties: tableSchema ? tableSchema.properties : [],
    creditFacilityId,
  });

  /**
   * queryPageNumber - page number of query results to fetch, separate from table page
   * queryPageBoundaryReceivableIds - array of receivable ids that are the last result of its query page, queryPageBoundaryReceivableIds[0] represents last recId in query page 1
   * queryPageBoundaryReceivableIds[n] === null indicates there are no more query pages after
   */
  const refetchReceivables = useCallback(
    ({
      queryPageNumber,
      queryPageBoundaryReceivableIds,
    }: {
      queryPageNumber: number;
      queryPageBoundaryReceivableIds: Util.Maybe<string>[];
    }) => {
      // this is a fresh query, should only fetch receivable count on initial query
      if (
        queryPageNumber === 1 &&
        queryPageBoundaryReceivableIds.length === 0
      ) {
        setLoadingInitialReceivables(true);
        setReceivablesCount(null);
        getReceivablesCountData({
          variables: {
            organizationId,
            creditFacilityId,
            loanProductId: loanProductId === 'all' ? null : loanProductId,
            reportingDatesRange,
            queryFilters:
              activeQueryFilterStrings.length > 0
                ? JSON.stringify(activeQueryFilterStrings)
                : null,
          },
        });
      }

      getReceivablesData({
        variables: {
          organizationId,
          creditFacilityId,
          loanProductId: loanProductId === 'all' ? null : loanProductId,
          reportingDatesRange,
          queryFilters:
            activeQueryFilterStrings.length > 0
              ? JSON.stringify(activeQueryFilterStrings)
              : null,
          queryPageBoundaryReceivableIds,
          queryPageNumber,
        },
      });
    },
    [
      organizationId,
      creditFacilityId,
      getReceivablesData,
      loanProductId,
      reportingDatesRange,
      activeQueryFilterStrings,
      setReceivablesCount,
      getReceivablesCountData,
    ],
  );

  const [
    inProgressJob,
    setInProgressJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  // initiatedJobInfo is used to display alert when a job is first initiated, before jobs are polled next
  const [initiatedJobInfo, setInitiatedJobInfo] = useState<{
    id: string;
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
    receivableCount: number | null;
  } | null>(null);

  const [
    terminalJob,
    setTerminalJob,
  ] = useState<AssignmentConfirmationJobGQLType | null>(null);

  const [
    receivableCountForConfirmationJob,
    setReceivableCountForConfirmationJob,
  ] = useState<number | null>(null);

  useEffect(() => {
    if (initiatedJobInfo) {
      setTerminalJob(null);
      setReceivableCountForConfirmationJob(initiatedJobInfo.receivableCount);
    }
  }, [initiatedJobInfo]);

  const [
    initiateReceivableReassignmentFromAthenaQuery,
  ] = useInitiateReceivableReassignmentFromAthenaQueryMutation({
    onCompleted: (data) => {
      if (initiatedJobInfo) {
        setInitiatedJobInfo({
          id: data.initiateReceivableReassignmentFromAthenaQuery.jobId,
          jobType: initiatedJobInfo.jobType,
          receivableCount:
            data.initiateReceivableReassignmentFromAthenaQuery.receivableCount,
        });
      }
    },
    onError: () => {
      if (!toast.isActive(errorToastId) && initiatedJobInfo) {
        errorToastConfig.title = `An unexpected error occurred during receivable ${initiatedJobInfo.jobType}. Please contact Finley for support.`;
        toast(errorToastConfig);
      }
      setInitiatedJobInfo(null);
    },
  });
  type InitiateReassignmentConfirmationJobParams = {
    rowSelectionData: any;
    areAllQueryRowsSelected: boolean;
    lpCfReassignmentInfo: {
      loanProductId: string;
      creditFacilityId: string | null;
    }[];
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
  };
  const initiateReassignmentConfirmationJob = useCallback(
    ({
      rowSelectionData,
      areAllQueryRowsSelected,
      lpCfReassignmentInfo,
      jobType,
    }: InitiateReassignmentConfirmationJobParams) => {
      // has to be defined within the callback otherwise infinitely re-renders
      const queryFilters = initializeReceivableResponseTapeQueryFilterLocalStorage(
        { organizationId, creditFacilityId },
      )[`${organizationId}+${creditFacilityId}`];
      const activeQueryFilters = queryFilters.filter(
        (queryFilter) =>
          queryFilter.isActive && queryFilter.query.rules.length > 0,
      );
      const activeQueryFilterStrs =
        activeQueryFilters.length > 0
          ? activeQueryFilters.map((queryFilter) =>
              formatAthenaSqlString(
                queryFilter.query,
                tableSchema?.properties || [],
              ),
            )
          : null;

      const finleyReceivableIdsToInclude: string[] = !areAllQueryRowsSelected
        ? Object.keys(rowSelectionData)
        : [];
      const finleyReceivableIdsToExclude: string[] = areAllQueryRowsSelected
        ? Object.keys(rowSelectionData)
        : [];

      // set dummy initial job info to display alert
      // then set actual initiated job info after it's kicked off
      setInitiatedJobInfo({
        id: 'job-id',
        jobType,
        receivableCount: null,
      });

      initiateReceivableReassignmentFromAthenaQuery({
        variables: {
          organizationId,
          creditFacilityId,
          loanProductId: loanProductId === 'all' ? null : loanProductId,
          reportingDatesRange,
          queryFilters: activeQueryFilterStrs
            ? JSON.stringify(activeQueryFilterStrs)
            : null,
          finleyReceivableIdsToInclude,
          finleyReceivableIdsToExclude,
          allQueryRowsSelected: areAllQueryRowsSelected,
          reassignmentInfo: lpCfReassignmentInfo,
        },
      });
    },
    [
      organizationId,
      creditFacilityId,
      initiateReceivableReassignmentFromAthenaQuery,
      loanProductId,
      reportingDatesRange,
      tableSchema?.properties,
    ],
  );

  useEffect(() => {
    refetchReceivables({
      queryPageNumber: 1,
      queryPageBoundaryReceivableIds: [],
    });
  }, [refetchReceivables]);

  const queryFilters =
    initializeReceivableResponseTapeQueryFilterLocalStorage({
      organizationId,
      creditFacilityId,
    })[creditFacilityId] || [];

  if (receivablesError) {
    // only deactivate filters + refresh page if there are any active filters
    if (queryFilters.length > 0) {
      deactivateAllReceivableResponseTapeQueryFiltersInLocalStorage({
        organizationId,
        creditFacilityId,
      });
      setTimeout(() => {
        navigate(0);
      }, 2000);
    }
  }

  const { receivables, paginationMetadata } = parseReceivablesData(
    receivablesData?.latestHistoricalReceivableResponseTapeValues,
  );

  const { repurchaseAndTransferJobs } = usePollRepurchaseAndTransferJobs({
    implementationId,
  });

  useEffect(() => {
    const inProgressJobs = repurchaseAndTransferJobs.filter(
      (job) => job.status === 'inProgress',
    );
    finleyAssert(
      inProgressJobs.length <= 1,
      `Expected maximum one repurchase or transfer confirmation job to be in progress for [implementationId ${implementationId}]`,
    );
    let terminatedJob: AssignmentConfirmationJobGQLType | null = null;
    if (inProgressJobs.length === 1) {
      // a job is in progress
      setInProgressJob(inProgressJobs[0]);
      setInitiatedJobInfo(null);
      setTerminalJob(null);
    } else if (inProgressJob) {
      // a job was in progress and should now be in terminal status
      const jobId = inProgressJob.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      finleyAssert(jobRecord, `Expected to find job with id ${jobId}`);
      terminatedJob = jobRecord;
    } else if (initiatedJobInfo) {
      // job was kicked off in UI, but either we polled and the job record doesn't exist yet
      // or the job completed in between polls
      const jobId = initiatedJobInfo.id;
      const jobRecord = repurchaseAndTransferJobs.find(
        (job) => job.id === jobId,
      );
      terminatedJob = jobRecord ?? null;
    }
    if (terminatedJob) {
      setTerminalJob(terminatedJob);
      setInProgressJob(null);
      setInitiatedJobInfo(null);
      if (terminatedJob.status === 'success') {
        refetchReceivables({
          queryPageNumber: 1,
          queryPageBoundaryReceivableIds: [],
        });
      }
    }
  }, [
    implementationId,
    inProgressJob,
    initiatedJobInfo,
    refetchReceivables,
    repurchaseAndTransferJobs,
  ]);

  const queryReceivablesCount = receivablesCountData
    ? receivablesCountData.latestHistoricalReceivableResponseTapeValuesCount
    : null;

  return (
    <>
      {initiatedJobInfo || inProgressJob ? (
        <Alert status="warning" m="32px 0">
          <AlertIcon />
          <AlertDescription>
            Receivable {initiatedJobInfo?.jobType || inProgressJob?.jobType} in
            progress.
          </AlertDescription>
        </Alert>
      ) : null}
      {!initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'success' &&
      receivableCountForConfirmationJob !== null ? (
        <Alert status="success" m="32px 0" justifyContent="space-between">
          <Flex gap={1}>
            <AlertIcon />
            <AlertTitle>
              {receivableCountForConfirmationJob}
              {receivableCountForConfirmationJob === 1
                ? ' receivable has '
                : ' receivables have '}
              been successfully{' '}
              {`${
                terminalJob.jobType === 'repurchase'
                  ? `repurchased and ${
                      receivableCountForConfirmationJob === 1 ? 'is' : 'are'
                    } now on the balance sheet.`
                  : 'transferred.'
              }`}
            </AlertTitle>
          </Flex>
          <CloseButton
            alignSelf="flex-start"
            position="relative"
            right={-1}
            onClick={() => setTerminalJob(null)}
          />
        </Alert>
      ) : null}
      {!initiatedJobInfo &&
      !inProgressJob &&
      terminalJob &&
      terminalJob.status === 'error' ? (
        <Alert status="error" m="32px 0">
          <Flex gap={1}>
            <AlertIcon />
            <AlertTitle>
              An unexpected error occurred during receivable{' '}
              {terminalJob?.jobType}. Please contact Finley for support.
            </AlertTitle>
          </Flex>
          <CloseButton
            alignSelf="flex-start"
            position="relative"
            right={-1}
            onClick={() => setTerminalJob(null)}
          />
        </Alert>
      ) : null}
      {!tableSchema ||
      (areReceivablesLoading && !receivablesError) ||
      (isReceivablesCountLoading && !receivablesCountError) ? (
        <Alert status="info" m="32px 0">
          <AlertIcon />
          <AlertDescription>
            Your receivables are loading and should be ready in just a few
            seconds.
          </AlertDescription>
        </Alert>
      ) : null}
      {receivablesError || receivablesCountError ? (
        <Alert status="error" m="32px 0">
          <AlertIcon />
          <AlertTitle>
            {queryFilters.length > 0 ? (
              <Text>
                We&apos;ve encountered an issue with our filters. Temporarily,
                filters are disabled while we refresh the data without them.
              </Text>
            ) : (
              <Text>
                We&apos;ve encountered an issue.{' '}
                <Link
                  href="mailto:support@finleycms.com"
                  color={colors.info}
                  textDecoration="underline"
                >
                  Contact
                </Link>{' '}
                your Finley Customer Service Manager for assistance.
              </Text>
            )}
          </AlertTitle>
        </Alert>
      ) : null}
      {!tableSchema && <Skeleton marginTop="2rem" height="30rem" />}
      {tableSchema && !receivablesError && !receivablesCountError ? (
        <ReceivablesTableBody
          organizationId={organizationId}
          creditFacilityId={creditFacilityId}
          loanProductId={loanProductId}
          paginationMetadata={paginationMetadata}
          receivables={receivables}
          columns={columns}
          reportingDatesRange={reportingDatesRange}
          setDateRangeFilter={setDateRangeFilter}
          refetchReceivables={refetchReceivables}
          isLoading={areReceivablesLoading}
          receivablesError={receivablesError}
          receivablesTableSchema={tableSchema}
          isLenderPage={isLenderPage}
          setInitiatedJobInfo={setInitiatedJobInfo}
          jobInProgress={!!inProgressJob || !!initiatedJobInfo}
          queryReceivablesCount={queryReceivablesCount}
          initiateReassignmentConfirmationJob={
            initiateReassignmentConfirmationJob
          }
          loadingInitialReceivables={loadingInitialReceivables}
          refetchActiveQueryFilters={refetchActiveQueryFilters}
        />
      ) : null}
    </>
  );
};

export const ReceivablesTable = ({
  creditFacilitySlug,
  organizationId,
  creditFacilityId,
  ...rest
}: ReceivablesTableProps & { creditFacilitySlug: string }) => {
  const { user } = useUserAuthOrThrow();
  const athenaSchemaQuery = useGetAthenaSchemaForOrganizationQuery({
    fetchPolicy: 'cache-first',
    variables: {
      organizationId: user.organization.id,
    },
  });
  if (athenaSchemaQuery.error) throw athenaSchemaQuery.error;

  const tableSchema = useMemo(() => {
    const tablePrefix: DataLake.TablePrefix = 'receivable_response_tapes';
    const tableSchemaName = `${tablePrefix}_${creditFacilitySlug.toLowerCase()}`;
    return athenaSchemaQuery.data?.athenaTables.find(
      (schema) => schema.tableName === tableSchemaName,
    );
  }, [athenaSchemaQuery.data?.athenaTables, creditFacilitySlug]);

  const [activeQueryFilterStrings, setActiveQueryFilterStrings] = useState<
    string[] | null
  >(null);

  const refetchActiveQueryFilters = useCallback(() => {
    const queryFilters = initializeReceivableResponseTapeQueryFilterLocalStorage(
      { organizationId, creditFacilityId },
    )[`${organizationId}+${creditFacilityId}`];
    const activeQueryFilters = queryFilters.filter(
      (queryFilter) =>
        queryFilter.isActive && queryFilter.query.rules.length > 0,
    );
    const activeQueryFilterStrs =
      activeQueryFilters.length > 0
        ? activeQueryFilters.map((queryFilter) =>
            formatAthenaSqlString(
              queryFilter.query,
              tableSchema?.properties || [],
            ),
          )
        : [];

    setActiveQueryFilterStrings(activeQueryFilterStrs);
  }, [creditFacilityId, organizationId, tableSchema?.properties]);

  useEffect(() => {
    if (
      !activeQueryFilterStrings ||
      (activeQueryFilterStrings && activeQueryFilterStrings.length > 0)
    ) {
      refetchActiveQueryFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchActiveQueryFilters]);

  // if there are active query filters, we need to wait for table schema before fetching receivables
  // otherwise we can fetch receivables and table schema in parallel
  if (
    !activeQueryFilterStrings ||
    (!tableSchema &&
      activeQueryFilterStrings &&
      activeQueryFilterStrings.length > 0)
  ) {
    return <Skeleton marginTop="2rem" height="30rem" />;
  }
  return (
    <ReceivablesTableBase
      tableSchema={tableSchema}
      organizationId={organizationId}
      creditFacilityId={creditFacilityId}
      activeQueryFilterStrings={activeQueryFilterStrings}
      refetchActiveQueryFilters={refetchActiveQueryFilters}
      {...rest}
    />
  );
};
